import {
  licensePlateStates,
  tractorEquipmentTypes,
  trailerEquipmentTypes,
} from 'pages/equipment/data/equipment-options'
import { caPorts } from './ace-trip-ca-ports'
import { packagingUnits } from './ace-trip-packaging-units'
import { usPorts } from './ace-trip-us-ports'

export type ManifestKind = 'ACE' | 'ACI'

export type AceParty = {
  name: string
  address: string
  city: string
  stateProvince: string
  postalCode: string
}

export type AceCommodity = {
  description: string
  quantity: string
  packagingUnit: string
  weight: string
  weightUnit: string
  loadedOn: string
}

export type AceShipment = {
  shipmentType: string
  shipmentControlNumber: string
  provinceOfLoading: string
  shipper: AceParty
  consignee: AceParty
  commodities: AceCommodity[]
}

export type AceTrailer = {
  trailerNumber: string
  trailerType: string
  licensePlateNumber: string
  licensePlateState: string
}

export type AceTripFormValues = {
  manifestKind: ManifestKind
  tripNumber: string
  usPortOfArrival: string
  estimatedArrival: string
  truckNumber: string
  truckType: string
  vinNumber: string
  truckLicensePlateNumber: string
  truckLicensePlateState: string
  trailer: AceTrailer
  driverNumber?: string
  driverFirstName: string
  driverLastName: string
  gender: string
  dateOfBirth: string
  citizenshipCountry: string
  fastCardNumber: string
  autoSend: boolean
  shipments: AceShipment[]
}

export type AceSelectOption = { label: string; value: string }

export const emptyTrailer = (): AceTrailer => ({
  trailerNumber: '',
  trailerType: '',
  licensePlateNumber: '',
  licensePlateState: '',
})

export const emptyParty = (): AceParty => ({
  name: '',
  address: '',
  city: '',
  stateProvince: '',
  postalCode: '',
})

export const emptyCommodity = (): AceCommodity => ({
  description: '',
  quantity: '',
  packagingUnit: '',
  weight: '',
  weightUnit: 'L',
  loadedOn: '',
})

export const emptyShipment = (): AceShipment => ({
  shipmentType: '',
  shipmentControlNumber: '',
  provinceOfLoading: '',
  shipper: emptyParty(),
  consignee: emptyParty(),
  commodities: [emptyCommodity()],
})

export const defaultAceTripFormValues: AceTripFormValues = {
  manifestKind: 'ACE',
  tripNumber: '',
  usPortOfArrival: '',
  estimatedArrival: '',
  truckNumber: '',
  truckType: '',
  vinNumber: '',
  truckLicensePlateNumber: '',
  truckLicensePlateState: '',
  trailer: emptyTrailer(),
  driverFirstName: '',
  driverLastName: '',
  gender: '',
  dateOfBirth: '',
  citizenshipCountry: '',
  fastCardNumber: '',
  autoSend: true,
  shipments: [emptyShipment()],
}

export const ACE_STEPS = ['Details', 'Shipments'] as const

export const GENDER_OPTIONS: AceSelectOption[] = [
  { label: 'M', value: 'M' },
  { label: 'F', value: 'F' },
]

export const SHIPMENT_TYPE_OPTIONS: AceSelectOption[] = [
  { label: 'PAPS', value: 'PAPS' },
  { label: 'PARS', value: 'PARS' },
  { label: 'In-Bond', value: 'IN_BOND' },
  { label: 'Section 321', value: 'SECTION_321' },
  { label: 'Informal', value: 'INFORMAL' },
  { label: 'Empty Equipment', value: 'EMPTY' },
]

export const PACKAGING_UNIT_OPTIONS = packagingUnits

export const US_PORT_OPTIONS = usPorts

export const CA_PORT_OPTIONS = caPorts

export const ACE_WEIGHT_UNITS = [
  'L',
  'LB',
  'LBS',
  'LBR',
  'K',
  'KG',
  'KGM',
  'MT',
  'TNE',
] as const

export const ACE_WEIGHT_UNIT_PATTERN = /^(L|LB|LBS|LBR|K|KG|KGM|MT|TNE)$/

const WEIGHT_UNIT_ALIASES: Record<string, string> = {
  L: 'L',
  LB: 'L',
  LBS: 'L',
  LBR: 'L',
  K: 'K',
  KG: 'KG',
  KGS: 'KG',
  KGM: 'KGM',
  MT: 'MT',
  T: 'TNE',
  TNE: 'TNE',
  TON: 'TNE',
  TONS: 'TNE',
  TONNE: 'TNE',
  TONNES: 'TNE',
  POUND: 'L',
  POUNDS: 'L',
}

export const toAceWeightUnitInput = (value: string) => {
  const next = value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3)
  if (!next) return ''
  if (next === 'LB' || next === 'LBS' || next === 'LBR') return 'L'
  if (ACE_WEIGHT_UNITS.some((unit) => unit.startsWith(next))) return next
  for (let i = next.length - 1; i > 0; i -= 1) {
    const prefix = next.slice(0, i)
    if (ACE_WEIGHT_UNITS.some((unit) => unit.startsWith(prefix))) return prefix
  }
  return ''
}

export const toAceWeightUnit = (value?: string | null) => {
  const compact = (value ?? '').trim().toUpperCase().replace(/[^A-Z]/g, '')
  if (!compact) return ''
  if (ACE_WEIGHT_UNIT_PATTERN.test(compact)) {
    return WEIGHT_UNIT_ALIASES[compact] ?? compact
  }
  return WEIGHT_UNIT_ALIASES[compact] ?? ''
}

export const LICENSE_PLATE_STATES = [...licensePlateStates].sort((a, b) =>
  a.name.localeCompare(b.name)
)

export const CANADIAN_PROVINCES = licensePlateStates.filter(
  (state) => state.country === 'CA'
)

export const US_STATES = licensePlateStates.filter(
  (state) => state.country === 'US'
)

export const TRUCK_TYPE_OPTIONS = tractorEquipmentTypes
export const TRAILER_TYPE_OPTIONS = trailerEquipmentTypes

export const buildManifestTripNumber = (
  kind: ManifestKind,
  scacCode: string,
  carrierCode: string,
  numericTripId: string | number
) => {
  const prefix = (kind === 'ACE' ? scacCode : carrierCode)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 4)
  const idDigits = String(numericTripId ?? '').replace(/\D/g, '') || '0'
  const paddedId = idDigits.padStart(4, '0')
  return `${prefix}${paddedId}`
}
